/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import { FiX, FiTrash2, FiCheckCircle, FiAlertTriangle } from "react-icons/fi";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";

export type FileUploadStatus = "uploading" | "success" | "error";

export interface FileUploadListItemProps {
  /** Name shown at the top of the row. */
  fileName: string;

  /** Drives the status line, progress-bar color, and which action shows. */
  status: FileUploadStatus;

  /** 0–100. Used while uploading; success/error render a full bar. */
  progress?: number;

  /** Overrides the default status-line text (e.g. a server error message). */
  statusText?: string;

  /** Cancels an in-progress upload. Shown as an X while status is "uploading". */
  onCancel?: () => void;

  /** Removes a finished file. Shown as a trash can when not uploading. */
  onRemove?: () => void;
}

const DEFAULT_STATUS_TEXT: Record<FileUploadStatus, string> = {
  uploading: "Uploading...",
  success: "Upload successful",
  error: "Upload interrupted: Check your connection and try again.",
};

/**
 * A single row in the upload modal's file list: name, an upload-status line with
 * a colored progress bar, and a cancel (while uploading) or remove (once
 * finished) action. The modal owns the upload state and
 * wires the callbacks.
 */
export const FileUploadListItem = ({
  fileName,
  status,
  progress,
  statusText,
  onCancel,
  onRemove,
}: FileUploadListItemProps): JSX.Element => {
  const { currentTheme } = useDexterTheme();

  const statusColor = {
    uploading: currentTheme.colors.primaryButtonColor,
    success: currentTheme.colors.positive,
    error: currentTheme.colors.negative,
  }[status];

  const barValue = status === "uploading" ? Math.min(progress ?? 0, 100) : 100;

  const message = statusText ?? DEFAULT_STATUS_TEXT[status];

  const showCancel = status === "uploading" && Boolean(onCancel);
  const showRemove = status !== "uploading" && Boolean(onRemove);

  return (
    <div
      style={{
        border: `1px solid ${currentTheme.colors.dropdownBorderColor}`,
        borderRadius: "6px",
        padding: currentTheme.spacing.md,
        display: "flex",
        flexDirection: "column",
        gap: currentTheme.spacing.sm,
        backgroundColor: currentTheme.colors.pageBackgroundColor,
      }}
    >
      {/* Name + action */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: currentTheme.spacing.sm,
        }}
      >
        <span
          style={{
            fontFamily: currentTheme.fonts.primary,
            fontSize: currentTheme.fontSizes.medium,
            fontWeight: 600,
            color: currentTheme.colors.defaultTextColor,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={fileName}
        >
          {fileName}
        </span>

        {showCancel && (
          <button
            type="button"
            aria-label="Cancel upload"
            title="Cancel upload"
            onClick={onCancel}
            style={iconButtonStyle(currentTheme.colors.secondaryTextColor)}
          >
            <FiX size={18} />
          </button>
        )}
        {showRemove && (
          <button
            type="button"
            aria-label="Remove file"
            title="Remove file"
            onClick={onRemove}
            style={iconButtonStyle(currentTheme.colors.secondaryTextColor)}
          >
            <FiTrash2 size={18} />
          </button>
        )}
      </div>

      {/* Status line */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: currentTheme.spacing.xs,
          color: statusColor,
          fontFamily: currentTheme.fonts.primary,
          fontSize: currentTheme.fontSizes.small,
        }}
      >
        {status === "success" && <FiCheckCircle size={16} />}
        {status === "error" && <FiAlertTriangle size={16} />}
        <span>{message}</span>
      </div>

      {/* Progress bar */}
      <div
        role="progressbar"
        aria-valuenow={barValue}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{
          height: "4px",
          borderRadius: "2px",
          backgroundColor: currentTheme.colors.backgroundTertiary,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${barValue}%`,
            height: "100%",
            backgroundColor: statusColor,
            transition: "width 150ms linear",
          }}
        />
      </div>
    </div>
  );
};

const iconButtonStyle = (color: string): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: 0,
  color,
  flexShrink: 0,
});
